import React, { useState, useMemo } from "react";
import { ArrowUpRight, Plus, X } from "lucide-react";
import caseStudies from "../data/casestudydata"; // import your data file
import { Link } from "react-router-dom";

export default function CasestudyAll() {
    const filterCategories = useMemo(() => {
        const industriesSet = new Set();
        const capabilitiesSet = new Set();
        caseStudies.forEach((item) => {
            if (item.industry) industriesSet.add(item.industry);
            if (item.capability) capabilitiesSet.add(item.capability);
        });
        return {
            Industries: Array.from(industriesSet).sort(),
            Capabilities: Array.from(capabilitiesSet).sort(),
        };
    }, []);
    const categoryNames = useMemo(() => Object.keys(filterCategories), [filterCategories]);
    const [activeCategory, setActiveCategory] = useState("");
    const [selectedFilters, setSelectedFilters] = useState({
        Industries: [],
        Capabilities: [],
    });
    const handleCategoryClick = (categoryName) => {
        setActiveCategory((prev) => (prev === categoryName ? null : categoryName));
    };
    const toggleFilter = (category, subOption) => {
        setSelectedFilters((prev) => {
            const currentList = prev[category] || [];
            const updatedList = currentList.includes(subOption)
                ? currentList.filter((item) => item !== subOption)
                : [...currentList, subOption];

            return {
                ...prev,
                [category]: updatedList,
            };
        });
    };
    const handleClearAll = () => {
        setSelectedFilters({
            Industries: [],
            Capabilities: [],
        });
    };
    const totalSelectedCount = useMemo(() => {
        return Object.values(selectedFilters).reduce((acc, curr) => acc + curr.length, 0);
    }, [selectedFilters]);

    const filteredCaseStudies = useMemo(() => {
        return caseStudies.filter((study) => {
            const selectedInd = selectedFilters.Industries || [];
            const selectedCap = selectedFilters.Capabilities || [];
            const matchesIndustry = selectedInd.length === 0 || selectedInd.includes(study.industry);
            const matchesCapability = selectedCap.length === 0 || selectedCap.includes(study.capability);
            return matchesIndustry && matchesCapability;
        });
    }, [selectedFilters]);

    return (
        <div className="w-full min-h-screen relative py-10 px-4 md:px-12">
            <div
                className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
                style={{
                    background:
                        "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
                }}
            />
            <div className="xl:w-4/5 mx-auto space-y-6">
                <div className="relative rounded-2xl bg-white border border-slate-200/80 shadow-sm p-2">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                            {categoryNames.map((catName) => {
                                const isOpen = activeCategory === catName;
                                const count = selectedFilters[catName]?.length || 0;

                                return (
                                    <button
                                        key={catName}
                                        onClick={() => handleCategoryClick(catName)}
                                        className={` group relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 border ${isOpen
                                            ? "bg-[#000036] text-white border-[#000036] shadow-md"
                                            : "bg-transparent text-slate-600 border-transparent hover:bg-slate-50 hover:text-[#000036]"
                                            }
                        `}
                                    >
                                        <span
                                            className={`
                                flex items-center justify-center
                                w-5 h-5 rounded-md
                                transition-all duration-200
                                ${isOpen
                                                    ? "bg-white/10"
                                                    : "bg-slate-100 group-hover:bg-white"
                                                }
                            `}
                                        >
                                            {isOpen ? (
                                                <X
                                                    className="w-3.5 h-3.5"
                                                    strokeWidth={2.5}
                                                />
                                            ) : (
                                                <Plus
                                                    className="w-3.5 h-3.5"
                                                    strokeWidth={2.5}
                                                />
                                            )}
                                        </span>
                                        <span>{catName}</span>
                                        {count > 0 && (
                                            <span
                                                className={`min-w-5 h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold
                                    ${isOpen
                                                        ? "bg-white text-[#000036]"
                                                        : "bg-[#000036] text-white"
                                                    }
                                `}
                                            >
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="hidden lg:block w-px h-7 bg-slate-200" />
                        <button
                            onClick={handleClearAll}
                            disabled={totalSelectedCount === 0}
                            className={`shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200
                ${totalSelectedCount > 0
                                    ? "text-slate-600 hover:text-red-600 hover:bg-red-50"
                                    : "text-slate-300 cursor-not-allowed"
                                }
            `}
                        >
                            <span>Clear Filters</span>
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
                {activeCategory && filterCategories[activeCategory] && (
                    <div className="mt-3 p-4 relative rounded-2xl bg-white border border-slate-200/70">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#3B9BD6]" />

                            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
                                Filter by {activeCategory}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {filterCategories[activeCategory].map((subOption) => {
                                const isSelected =
                                    selectedFilters[activeCategory]?.includes(subOption);
                                return (
                                    <button
                                        key={subOption}
                                        onClick={() =>
                                            toggleFilter(activeCategory, subOption)
                                        }
                                        className={` group flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs sm:text-sm font-medium transition-all duration-200
                            ${isSelected
                                                ? "bg-[#000036] text-white border-[#000036] shadow-sm"
                                                : "bg-white text-slate-600 border-slate-200 hover:border-[#3B9BD6] hover:text-[#000036] hover:shadow-sm"
                                            }`}
                                    >
                                        <span
                                            className={`relative w-4 h-4 rounded-full border flex items-center justify-center transition-all duration-200
                                ${isSelected
                                                    ? "border-[#5DC9F0] bg-[#5DC9F0]"
                                                    : "border-slate-300 bg-white group-hover:border-[#3B9BD6]"
                                                } `}
                                        >
                                            {isSelected && (
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#000036]" />
                                            )}
                                        </span>
                                        <span>{subOption}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className="pt-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCaseStudies.map((study) => (
                            <article key={study.id} className=" group relative border overflow-hidden border-slate-200/90 rounded-xl p-6 flex flex-col justify-between hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 bg-white">
                                <div className="relative z-10 space-y-3">
                                    <div className="relative w-full h-56 sm:h-64 rounded-xl overflow-hidden bg-slate-100 border border-slate-200/80">
                                        <img
                                            src={study.image}
                                            alt={study.title}
                                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 ease-out"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
                                        <div className="absolute bottom-4 left-4">
                                            <span className="font-bold text-xs uppercase tracking-wider text-white px-3 py-1 rounded-md shadow-xs ">
                                                {study.date}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-wrap mb-1">
                                        <span className="text-sm bg-[#000036] text-white px-2.5 py-1 rounded-full">
                                            {study.industry}
                                        </span>
                                        <span className="text-sm bg-[#000036] text-white px-2.5 py-1 rounded-full ">
                                            {study.capability}
                                        </span>
                                    </div>
                                    <div className="space-y-3">
                                        <h3 className="text-xl sm:text-2xl font-bold text-[#000036] tracking-tight leading-snug  transition-colors">
                                            {study.title}
                                        </h3>
                                        <p className="text-sm sm:text-base text-slate-600 font-normal leading-relaxed">
                                            {study.summary}
                                        </p>
                                    </div>
                                </div>
                                {study.stats?.[0] && (
                                    <div className="mt-2 pt-2 border-t border-gray-100 flex items-baseline gap-2">
                                        <span className="text-2xl font-black font-serif text-blue-900">
                                            {study.stats[0].number}
                                        </span>
                                        <span className="text-sm text-gray-500 leading-tight">
                                            {study.stats[0].tagline}
                                        </span>
                                    </div>
                                )}
                                <div className="relative z-10 pt-4 mt-4 border-t border-slate-100">
                                    <Link
                                        to={study.slug}
                                        className="group inline-flex items-center justify-between gap-3 px-6 py-2.5 border border-black/15 bg-white/70 text-slate-900 font-mono text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:bg-slate-950 hover:text-white hover:shadow-md rounded-full"
                                    >
                                        <span> Read More</span>
                                        <div className="h-6 w-6 rounded-full  flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1 group-hover:bg-white group-hover:text-slate-950">
                                            <ArrowUpRight size={13} />
                                        </div>
                                    </Link>
                                </div>

                            </article>
                        ))}
                    </div>
                    {filteredCaseStudies.length === 0 && (
                        <div className="text-center py-16 bg-white/60 rounded-2xl border border-dashed border-gray-300">
                            <p className="text-gray-500">No case studies match your active filters.</p>
                            <button
                                onClick={handleClearAll}
                                className="mt-3 text-sm text-blue-600 hover:underline font-semibold"
                            >
                                Clear all filters
                            </button>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}